/**
 * Google Analytics utility functions
 * Handles GA tracking with cookie consent verification
 */

import { demoUsers } from "../../functions/src/api/constants";

// Extend Window interface to include dataLayer
declare global {
    interface Window {
        dataLayer: any[];
    }
}

/**
 * Analytics event constants
 * Centralized event names to avoid magic strings
 */
export const ANALYTICS_EVENTS = {
    // Order completion events
    COMPLETE_ORDER_CLICKED: 'complete_order_clicked',
    ORDER_COMPLETED_SUCCESSFULLY: 'order_completed_successfully',
    ORDER_COMPLETION_ERROR: 'order_completion_error',
    
    // Order cancellation events
    CANCEL_ORDER_CLICKED: 'cancel_order_clicked',
    ORDER_CANCELLED_SUCCESSFULLY: 'order_cancelled_successfully',
    ORDER_CANCELLATION_ERROR: 'order_cancellation_error',
} as const;

/**
 * Analytics event labels
 * Centralized event labels for consistency
 */
export const ANALYTICS_LABELS = {
    // Order types
    NEW_ORDER: 'New Order',
    ORDER_CHANGE: 'Order Change',
    CANCEL_ORDER: 'Cancel Order',
    
    // Completion status
    NEW_ORDER_COMPLETED: 'New Order Completed',
    ORDER_CHANGE_COMPLETED: 'Order Change Completed',
    ORDER_CANCEL_COMPLETED: 'Order Cancel Completed',
    
    // Error status
    NEW_ORDER_ERROR: 'New Order Error',
    ORDER_CHANGE_ERROR: 'Order Change Error',
    ORDER_CANCEL_ERROR: 'Order Cancel Error',
} as const;

/**
 * Check if Google Analytics tracking is available and allowed
 * @param {string} userId - The current user ID to check if they're a demo user
 * @returns {boolean} True if cookies are accepted, dataLayer is available, and user is not a demo user
 */
export function isAnalyticsEnabled(userId?: string): boolean {
    // Check if user is a demo user
    if (userId && demoUsers.includes(userId)) {
        return false;
    }
    
    // Check if cookies are accepted
    return localStorage.getItem('cookiesConsent') === 'accepted';
}

/**
 * Track a Google Analytics event with consent verification
 * @param {string} eventName - The event name to track
 * @param {object} eventData - Event data object
 * @param {string} eventData.event_category - Event category
 * @param {string} eventData.event_label - Event label
 * @param {number} eventData.value - Event value
 * @param {object} eventData.custom_parameters - Custom parameters object
 * @param {string} userId - The current user ID to check if they're a demo user
 */
export function trackEvent(eventName: string, eventData: {
    event_category: string;
    event_label: string;
    value?: number;
    custom_parameters?: Record<string, any>;
}, userId?: string): void {
    if (isAnalyticsEnabled(userId)) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: eventName,
            event_category: eventData.event_category,
            event_label: eventData.event_label,
            value: eventData.value,
            ...eventData.custom_parameters
        });
    }
}

/**
 * Track order-related events with standard parameters
 * @param {string} eventName - The event name to track
 * @param {string} eventLabel - Event label
 * @param {string} schoolKey - School key
 * @param {string} orderType - Order type ('package' or 'normal')
 * @param {number} value - Event value (default: 1)
 * @param {string} userId - The current user ID to check if they're a demo user
 */
export function trackOrderEvent(
    eventName: string, 
    eventLabel: string, 
    schoolKey: string, 
    orderType: string, 
    userId?: string,
    value: number = 1   
): void {
    trackEvent(eventName, {
        event_category: 'Order',
        event_label: eventLabel,
        value: value,
        custom_parameters: {
            school_key: schoolKey || 'unknown',
            order_type: orderType
        }
    }, userId);
}

/**
 * Track general events with custom parameters
 * @param {string} eventName - The event name to track
 * @param {string} category - Event category
 * @param {string} label - Event label
 * @param {object} customParams - Custom parameters
 * @param {number} value - Event value (default: 1)
 */
export function trackCustomEvent(
    eventName: string,
    category: string,
    label: string,
    customParams: Record<string, any> = {},
    value: number = 1
): void {
    trackEvent(eventName, {
        event_category: category,
        event_label: label,
        value: value,
        custom_parameters: customParams
    });
}
